import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, MoreHorizontal } from "lucide-react";
import "./HeaderPanel.scss";

const MORE_GAP = 6;

export const hasMoreBelow = (el) => Boolean(el) && el.scrollHeight - el.scrollTop - el.clientHeight > MORE_GAP;

const PanelMenu = ({ items }) => {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (wrap.current && !wrap.current.contains(event.target)) setOpen(false);
    };
    const escape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return (
    <div className="hp__menu" ref={wrap}>
      <button
        type="button"
        className="hp__menu-toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <MoreHorizontal size={18} strokeWidth={1.75} aria-hidden="true" />
      </button>
      {open ? (
        <div className="hp__menu-list" role="menu">
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`hp__menu-item${item.danger ? " is-danger" : ""}`}
              onClick={(event) => {
                event.stopPropagation();
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export const PanelSection = ({ label, children }) => (
  <section className="hp__section" aria-label={label}>
    <h3 className="hp__section-label">{label}</h3>
    {children}
  </section>
);

export const PanelRow = ({ unread = false, children, note = null }) => (
  <div className={`hp-row${unread ? " is-unread" : ""}`} data-unread={unread ? "true" : "false"}>
    {children}
    {note ? <p className="hp-row__note">{note}</p> : null}
  </div>
);

export const PanelSkeleton = ({ rows = 4 }) => (
  <div className="hp__skeleton" aria-busy="true" aria-label="Loading">
    {Array.from({ length: rows }, (_, index) => (
      <div className="hp__skeleton-row" key={index}>
        <span className="hp__skeleton-avatar" />
        <span className="hp__skeleton-lines">
          <span />
          <span />
        </span>
      </div>
    ))}
  </div>
);

const HeaderPanel = ({ title, menuItems = [], footer = null, children, bodyRef = null, onBodyScroll = null, className = "" }) => {
  const ownRef = useRef(null);
  const [more, setMore] = useState(false);

  const setRef = useCallback(
    (node) => {
      ownRef.current = node;
      if (typeof bodyRef === "function") bodyRef(node);
      else if (bodyRef) bodyRef.current = node;
    },
    [bodyRef],
  );

  const measure = useCallback(() => setMore(hasMoreBelow(ownRef.current)), []);

  useLayoutEffect(() => {
    measure();
  });

  useEffect(() => {
    const el = ownRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  return (
    <div className={`hp ${className}`.trim()} onClick={(event) => event.stopPropagation()}>
      <div className="hp__head">
        <h2 className="hp__title">{title}</h2>
        {menuItems.length ? <PanelMenu items={menuItems} /> : null}
      </div>
      <div className="hp__scroll">
        <div
          className="hp__body"
          ref={setRef}
          onScroll={(event) => {
            measure();
            if (onBodyScroll) onBodyScroll(event);
          }}
        >
          {children}
        </div>
        <div className={`hp__fade${more ? " is-on" : ""}`} aria-hidden="true" />
        {more ? (
          <button
            type="button"
            className="hp__more"
            onClick={(event) => {
              event.stopPropagation();
              const el = ownRef.current;
              if (el) el.scrollBy({ top: Math.round(el.clientHeight * 0.8), behavior: "smooth" });
            }}
          >
            More below
            <ChevronDown size={14} strokeWidth={1.75} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {footer ? (
        <div className="hp__foot">
          {footer.href ? (
            <a className="hp__foot-link" href={footer.href} onClick={(event) => event.stopPropagation()}>
              {footer.label}
            </a>
          ) : (
            <button
              type="button"
              className="hp__foot-link"
              onClick={(event) => {
                event.stopPropagation();
                footer.onClick();
              }}
            >
              {footer.label}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default HeaderPanel;
